import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import Button from "../../../common/button/Button";
import ToolsSelect from "../kit/ToolsSelect";
import WComponent from "../../../common/WComponent";
import FileDropZone from "../../../common/file_drop_zone/FileDropZone";
import UtilityService from "../../../../services/UtilityService";
import BookkeepingService from "../../../../services/BookkeepingService";
import CURRENCIES from "../../../../constants/Currencies";
import "./bank-account-review.css";

class BankAccountReview extends WComponent {
	constructor(props) {
		super(props);

		const detectedNames = props.detectedNames || [];
		const accounts = detectedNames.map(name => ({ name, included: true }));

		this.state = {
			accounts,
			fileByAccount: props.initialFileByAccount ? { ...props.initialFileByAccount } : {},
			currencyByAccount: props.initialCurrencyByAccount ? { ...props.initialCurrencyByAccount } : {},
			customName: "",
		};
	}

	getRemainingGlOptions = () => {
		const allAccountNames = this.props.allAccountNames || [];
		const usedNames = this.state.accounts.map(account => account.name);
		const remainingNames = allAccountNames.filter(name => !usedNames.includes(name));
		const options = remainingNames.map(name => ({ value: name, label: name }));
		return options;
	};

	currencyCodeFor = accountName => this.state.currencyByAccount[accountName] || BookkeepingService.guessAccountCurrency({ accountName });

	onSelectCurrency = (accountName, currencyCode) => {
		this.setState(prev => ({ currencyByAccount: { ...prev.currencyByAccount, [accountName]: currencyCode } }));
	};

	onToggleIncluded = index => {
		this.setState(prev => {
			const accounts = prev.accounts.map((account, accountIndex) =>
				accountIndex === index ? { ...account, included: !account.included } : account
			);
			return { accounts };
		});
	};

	onRenameAccount = (index, newName) => {
		this.setState(prev => {
			const previousName = prev.accounts[index].name;
			const accounts = prev.accounts.map((account, accountIndex) =>
				accountIndex === index ? { ...account, name: newName } : account
			);
			const fileByAccount = { ...prev.fileByAccount };
			const currencyByAccount = { ...prev.currencyByAccount };
			if (fileByAccount[previousName]) {
				fileByAccount[newName] = fileByAccount[previousName];
				delete fileByAccount[previousName];
			}
			if (currencyByAccount[previousName]) {
				currencyByAccount[newName] = currencyByAccount[previousName];
				delete currencyByAccount[previousName];
			}
			return { accounts, fileByAccount, currencyByAccount };
		});
	};

	onRemoveAccount = index => {
		this.setState(prev => {
			const removedName = prev.accounts[index].name;
			const accounts = prev.accounts.filter((account, accountIndex) => accountIndex !== index);
			const fileByAccount = { ...prev.fileByAccount };
			const currencyByAccount = { ...prev.currencyByAccount };
			delete fileByAccount[removedName];
			delete currencyByAccount[removedName];
			return { accounts, fileByAccount, currencyByAccount };
		});
	};

	onSelectFileForAccount = (accountName, event) => {
		const selectedFiles = Array.from(event.target.files || []);
		event.target.value = "";
		if (selectedFiles.length === 0) return;

		const pdfFiles = selectedFiles.filter(file => UtilityService.isPdfFile(file.name));
		const allArePdfs = pdfFiles.length === selectedFiles.length;

		if (allArePdfs) {
			this.setState(prev => ({ fileByAccount: { ...prev.fileByAccount, [accountName]: { files: pdfFiles, kind: "pdf" } } }));
			return;
		}

		const spreadsheetFile = selectedFiles.find(file => !UtilityService.isPdfFile(file.name));
		this.setState(prev => ({ fileByAccount: { ...prev.fileByAccount, [accountName]: { file: spreadsheetFile, kind: "spreadsheet" } } }));
	};

	onRemoveFileForAccount = accountName => {
		this.setState(prev => {
			const fileByAccount = { ...prev.fileByAccount };
			delete fileByAccount[accountName];
			return { fileByAccount };
		});
	};

	addAccountName = name => {
		const cleanName = (name || "").trim();
		if (!cleanName) return;
		const alreadyListed = this.state.accounts.some(account => account.name === cleanName);
		if (alreadyListed) return;
		this.setState(prev => ({ accounts: [...prev.accounts, { name: cleanName, included: true }] }));
	};

	addStatementFiles = fileList => {
		const files = Array.from(fileList || []);
		if (files.length === 0) return;

		this.setState(prev => {
			const accounts = [...prev.accounts];
			const fileByAccount = { ...prev.fileByAccount };
			for (const file of files) {
				const accountName = UtilityService.getFileNameWithoutExtension(file.name);
				const alreadyListed = accounts.some(account => account.name === accountName);
				if (!alreadyListed) accounts.push({ name: accountName, included: true });
				const isPdf = UtilityService.isPdfFile(file.name);
				fileByAccount[accountName] = isPdf ? { files: [file], kind: "pdf" } : { file, kind: "spreadsheet" };
			}
			return { accounts, fileByAccount };
		});
	};

	onAddFromGl = accountName => {
		this.addAccountName(accountName);
	};

	onCustomNameChange = event => {
		this.update({ customName: event.target.value });
	};

	onAddCustom = () => {
		this.addAccountName(this.state.customName);
		this.update({ customName: "" });
	};

	componentDidMount = () => {
		this.notifyDraft();
	};

	componentDidUpdate = (prevProps, prevState) => {
		const draftChanged = prevState.accounts !== this.state.accounts || prevState.currencyByAccount !== this.state.currencyByAccount || prevState.fileByAccount !== this.state.fileByAccount;
		if (draftChanged) this.notifyDraft();
	};

	buildContinuePayload = () => {
		const includedAccounts = this.state.accounts.filter(account => account.included && account.name.trim());
		const bankAccountNames = includedAccounts.map(account => account.name.trim());
		const fileByAccount = {};
		const currencyByAccount = {};
		for (const name of bankAccountNames) {
			if (this.state.fileByAccount[name]) fileByAccount[name] = this.state.fileByAccount[name];
			currencyByAccount[name] = this.currencyCodeFor(name);
		}
		return { bankAccountNames, fileByAccount, currencyByAccount, includedCount: bankAccountNames.length };
	};

	notifyDraft = () => {
		if (this.props.onDraftChange) this.props.onDraftChange(this.buildContinuePayload());
	};

	renderAccountRow = (account, index) => {
		const attachedFile = this.state.fileByAccount[account.name];
		const hasFile = !!(attachedFile && (attachedFile.file || (attachedFile.files && attachedFile.files.length)));
		const showFilePrompts = hasFile && attachedFile.kind === "spreadsheet" && !!this.props.renderFilePrompts;
		const uploadLabel = !hasFile
			? "Upload statement(s)"
			: attachedFile.kind === "pdf"
				? `${attachedFile.files.length} PDF statement${attachedFile.files.length === 1 ? "" : "s"}`
				: attachedFile.file.name;

		return (
			<div key={index} className={clsx("bank-review__row-wrap", !account.included && "bank-review__row-wrap--excluded")}>
				<div className="bank-review__row">
					<button
						type="button"
						className={clsx("bank-review__include", account.included && "bank-review__include--on")}
						title={account.included ? "Uploading a statement for this account" : "Skipped — no statement"}
						onClick={() => this.onToggleIncluded(index)}
					>
						{account.included ? <Icon.Check size={13} /> : null}
					</button>
					<input
						type="text"
						className="bank-review__name-input"
						value={account.name}
						onChange={event => this.onRenameAccount(index, event.target.value)}
					/>
					<label className="bank-review__upload">
						<span className={clsx("bank-review__upload-btn", hasFile && "bank-review__upload-btn--selected")}>
							<Icon.Upload size={13} />
							<span className="bank-review__upload-text">{uploadLabel}</span>
						</span>
						<input
							type="file"
							accept=".csv,.xlsx,.pdf"
							multiple
							className="bank-review__upload-input"
							onChange={event => this.onSelectFileForAccount(account.name, event)}
						/>
					</label>
					{hasFile && (
						<button type="button" className="bank-review__file-remove" title="Remove this statement" onClick={() => this.onRemoveFileForAccount(account.name)}>
							<Icon.X size={13} />
						</button>
					)}
					<span className="bank-review__currency" title="Currency of this account — non-CAD is converted to CAD at the Bank of Canada rate">
						<ToolsSelect
							value={this.currencyCodeFor(account.name)}
							options={CURRENCIES.map(currency => ({ value: currency.code, label: `${currency.code} — ${currency.name}` }))}
							onChange={currencyCode => this.onSelectCurrency(account.name, currencyCode)}
							searchable
							searchPlaceholder="Search currencies…"
						/>
					</span>
					<button type="button" className="bank-review__remove" title="Remove this account" onClick={() => this.onRemoveAccount(index)}>
						<Icon.X size={14} />
					</button>
				</div>
				{showFilePrompts && <div className="bank-review__sign-prompt">{this.props.renderFilePrompts(attachedFile.file)}</div>}
			</div>
		);
	};

	render() {
		const { accounts, customName } = this.state;

		return (
			<div className="bank-review">
				<div className="bank-review__header">
					<h3 className="bank-review__title">Confirm the Bank Accounts</h3>
					<p className="bank-review__subtitle">
						These are the accounts we think are real bank or credit card accounts. Uncheck any that aren't,
						rename them, set each one's currency, and upload its statement. Add any we missed below.
					</p>
				</div>

				<div className="bank-review__list">
					{accounts.length === 0 ? (
						<div className="bank-review__empty">No accounts yet — add the bank accounts below, or drop in statement files.</div>
					) : (
						accounts.map(this.renderAccountRow)
					)}
				</div>

				<FileDropZone
					className="bank-review__dropzone"
					accept=".csv,.xlsx,.pdf"
					multiple
					onFiles={this.addStatementFiles}
					title="Missed an account? Drop its statement here to add it"
					subtitle="each dropped file becomes a new account"
				/>

				<div className="bank-review__add">
					<div className="bank-review__add-title">Add a bank account</div>
					<ToolsSelect
						value=""
						options={this.getRemainingGlOptions()}
						placeholder="Pick from your general ledger accounts…"
						onChange={this.onAddFromGl}
						searchable
						searchPlaceholder="Search your general ledger…"
					/>
					<div className="bank-review__add-custom">
						<input
							type="text"
							className="tk-input bank-review__custom-input"
							placeholder="…or type an account name"
							value={customName}
							onChange={this.onCustomNameChange}
						/>
						<Button text="Add" view="secondary" onClick={this.onAddCustom} disabled={!customName.trim()} />
					</div>
				</div>
			</div>
		);
	}
}

export default BankAccountReview;
